import React from "react";
import { useParams, Link } from "wouter";
import { Button } from '../components/ui/button.js';
import { Card, CardContent } from '../components/ui/card.js';
import { AlertCircle } from "lucide-react";

interface Service {
  id: number;
  name: string;
  description: string;
  imageUrl: string;
  slug: string;
  features: string[];
  priceRange: string;
}

const services: Service[] = [
  {
    id: 1,
    name: "Phone Repair",
    description: "Advanced smartphone repair solutions for all major brands including iPhone, Samsung, Google Pixel, and more. Our technicians specialize in micro-soldering, component-level repairs, and precision screen replacements. We use OEM-quality parts and offer same-day service for most common repairs.",
    imageUrl: "https://images.unsplash.com/photo-1546054454-aa26e2b734c7?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    slug: "phone-repair",
    features: [
      "Screen Replacement",
      "Battery Replacement",
      "Charging Port Repair",
      "Camera Repair",
      "Button Repair",
      "Water Damage Repair",
      "Software Issues",
      "Data Recovery"
    ],
    priceRange: "$00 - $000"
  },
  {
    id: 2,
    name: "Computer Repair",
    description: "Comprehensive computer repair solutions for both laptops and desktops. From motherboard diagnostics and RAM upgrades to complete system restoration, our certified technicians deliver fast, reliable service. We specialize in liquid damage repair, operating system optimization, custom PC builds, and performance tuning for gaming rigs.",
    imageUrl: "https://images.unsplash.com/photo-1517430816045-df4b7de11d1d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    slug: "computer-repair",
    features: [
      "Hardware Upgrades",
      "Virus Removal",
      "Data Recovery",
      "Screen Replacement",
      "Keyboard Repair",
      "Battery Replacement",
      "Overheating Issues",
      "Software Installation"
    ],
    priceRange: "$00 - $000"
  },
  {
    id: 3,
    name: "Tablet Repair",
    description: "Professional repair services for tablets of all sizes and brands. We specialize in screen replacement, battery replacement, charging port repair, and software issues.",
    imageUrl: "https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    slug: "tablet-repair",
    features: [
      "Screen Replacement",
      "Battery Replacement",
      "Charging Port Repair",
      "Software Issues",
      "Button Repair",
      "Water Damage Repair",
      "Data Recovery",
      "Stylus Repair"
    ],
    priceRange: "$00 - $000"
  },
  {
    id: 4,
    name: "Game Console Repair",
    description: "Professional repair services for all major gaming consoles. We handle HDMI port repair, disc drive replacement, overheating issues, and controller repair. Our technicians are experts in console repairs.",
    imageUrl: "https://images.unsplash.com/photo-1486572788966-cfd3df1f5b42?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    slug: "game-console-repair",
    features: [
      "HDMI Port Repair",
      "Disc Drive Replacement",
      "Overheating Issues",
      "Controller Repair",
      "Button Repair",
      "Software Issues",
      "Power Supply Repair",
      "Data Recovery"
    ],
    priceRange: "$00 - $000"
  }
];

const ServiceDetails = () => {
  const { slug } = useParams();
  const service = services.find(s => s.slug === slug);

  if (!service) {
    return (
      <div className="container mx-auto px-4 py-16">
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6">
            <div className="flex mb-4 gap-2">
              <AlertCircle className="h-8 w-8 text-red-500" />
              <h1 className="text-2xl font-bold text-gray-900">Service Not Found</h1>
            </div>
            <p className="mt-4 text-sm text-gray-600">
              The service you are looking for does not exist or has been removed.
            </p>
            <Button className="mt-6 bg-primary hover:bg-primary/90">
              <Link href="/services">
                View All Services
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 pt-16" style={{ marginTop: '80px' }}>
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb Navigation */}
        <nav className="mb-6" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 text-sm text-gray-600">
            <li>
              <Link href="/" className="hover:text-primary transition-colors">
                Home
              </Link>
            </li>
            <li className="flex items-center">
              <span className="mx-2">/</span>
              <Link href="/services" className="hover:text-primary transition-colors">
                Services
              </Link>
            </li>
            <li className="flex items-center">
              <span className="mx-2">/</span>
              <span className="text-gray-900 font-medium">{service.name}</span>
            </li>
          </ol>
        </nav>
        
        <h1 className="text-3xl md:text-4xl font-bold mb-4">{service.name}</h1>
        <p className="text-lg text-gray-600 mb-12">
          {service.description}
        </p>

        <div className="flex flex-col md:flex-row gap-8">
          <div className="md:w-1/2">
            <img 
              src={service.imageUrl} 
              alt={service.name} 
              className="w-full h-auto rounded-lg mb-6"
            />
            <h2 className="text-2xl font-semibold mb-4">About This Service</h2>
            <p className="text-gray-600 mb-6">
              {service.name === "Computer Repair" 
                ? "Our computer repair service combines technical expertise with state-of-the-art diagnostic tools to identify and resolve issues efficiently. Whether you need emergency repairs or routine maintenance, we provide transparent pricing and quick turnaround times."
                : service.name === "Phone Repair" 
                ? "We understand how essential your phone is to daily life. Our repair service focuses on quality craftsmanship and customer satisfaction, using only premium parts and offering warranty protection on all repairs."
                : service.name === "Tablet Repair"
                ? "Tablet repairs require specialized knowledge and precision. Our technicians are trained to handle delicate components and complex circuitry unique to tablet devices."
                : "Gaming consoles are sophisticated systems that require expert care. Our repair technicians stay updated with the latest console technologies and repair techniques."
              }
            </p>
            <Button 
              className="bg-[#FF6633] hover:bg-[#FF6633]/90"
              asChild
            >
              <Link href="/booking">
                Book This Service
              </Link>
            </Button>
          </div>

          <div className="md:w-1/2">
            <h2 className="text-2xl font-semibold mb-4">Service Details</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">What We Fix</h3>
                <ul className="space-y-2">
                  {service.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <span className="w-4 h-4 rounded-full bg-primary flex-shrink-0"></span>
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Price Range</h3>
                <p className="text-gray-600">{service.priceRange}</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Warranty</h3>
                <p className="text-gray-600">- -</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetails;
